import React, { useEffect, useState, useRef } from "react";
import { MessageCircle, Send, Plus, ArrowLeft } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Loader, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Support() {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [subject, setSubject] = useState(""), [body, setBody] = useState("");
  const bottomRef = useRef();

  const loadConvs = async () => { const { data } = await api.get("/conversations"); setConvs(data); };
  useEffect(() => { loadConvs(); }, []);
  useEffect(() => { if (active) api.get(`/conversations/${active}/messages`).then((r) => setThread(r.data)); }, [active]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const start = async () => {
    if (!subject || !body) return toast.error("Please fill in a subject and message");
    const { data } = await api.post("/conversations", { subject, body });
    setNewOpen(false); setSubject(""); setBody("");
    await loadConvs(); setActive(data.id);
  };
  const send = async () => {
    if (!text.trim()) return;
    await api.post(`/conversations/${active}/messages`, { body: text });
    setText("");
    const { data } = await api.get(`/conversations/${active}/messages`); setThread(data);
    loadConvs();
  };

  if (convs === null) return <Loader />;

  if (active && thread) {
    return (
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" className="rounded-full mb-3 -ml-2" onClick={() => { setActive(null); setThread(null); }} data-testid="back-to-inbox"><ArrowLeft className="w-4 h-4 mr-1.5" /> All conversations</Button>
        <div className="bg-white border border-border rounded-2xl shadow-sm flex flex-col h-[70vh]">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-heading font-semibold">{thread.conversation.subject}</h2>
            <StatusBadge status={thread.conversation.status} />
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-thread">
            {thread.messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_role === "student" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${m.sender_role === "student" ? "bg-teal text-white rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  <p className={`text-[10px] mt-1 ${m.sender_role === "student" ? "text-white/70" : "text-muted-foreground"}`}>{new Date(m.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-border flex gap-2">
            <Input placeholder="Type your message..." value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="message-input" />
            <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={send} data-testid="send-message"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-heading text-3xl font-bold">Support</h1>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-teal hover:bg-teal/90" data-testid="new-conversation"><Plus className="w-4 h-4 mr-1.5" /> New</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Start a conversation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="new-subject" />
              <Textarea placeholder="How can we help?" value={body} onChange={(e) => setBody(e.target.value)} data-testid="new-body" />
            </div>
            <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={start} data-testid="send-inquiry">Send</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-teal/5 border border-teal/20 rounded-2xl p-5 mb-5">
        <p className="font-semibold">Need Help? We're Happy to Serve You!</p>
        <p className="text-sm text-muted-foreground mt-1">Have a question about the course or your learning progress? Send us a message and our team will get back to you as soon as possible.</p>
        <p className="text-xs text-muted-foreground mt-2">Please note: response times may vary depending on inquiry volume.</p>
      </div>

      {convs.length === 0 ? (
        <EmptyState icon={MessageCircle} title="No conversations yet" description="Start a new conversation and our team will reply here." testId="support-empty"
          action={<Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => setNewOpen(true)}>Start a conversation</Button>} />
      ) : (
        <div className="space-y-2">
          {convs.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} className="w-full bg-white border border-border rounded-2xl p-4 shadow-sm text-left hover:bg-secondary/40 transition-colors flex items-center justify-between gap-3" data-testid={`conv-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{c.subject}</p>
                  {c.unread_student && <span className="w-2 h-2 rounded-full bg-teal" />}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{new Date(c.last_message_at).toLocaleString()}</p>
              </div>
              <StatusBadge status={c.status} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
